import React, { PureComponent, ReactElement } from 'react';
import PropTypes from 'prop-types';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import throttle from 'lodash.throttle';

import './ServiceEntityList.scss';
import { BREAKPOINTS, PLACEHOLDER } from 'other/constants';
import { EmptyList } from 'components/common/EmptyList/EmptyList';
import { ShowMoreLess } from 'components/common/ShowMoreLess/ShowMoreLess';

import { EProviderMediaObjectType, TId } from 'types';

/**/
type Props<T> = {
  children: ReactElement;
  colCount?: number;
  counterToShowAll?: number;
  data: T[];
  gutter?: number;
  onChange?: (arr: T[], type: EProviderMediaObjectType) => void;
};
type State = {
  colCount: number;
  isExpanded: boolean;
  width: number;
};

/**
 *
 */
abstract class ServiceEntityList<T extends TId> extends PureComponent<
  Props<T>,
  State
> {
  static propTypes;
  static defaultProps;
  private readonly delegateResize: Function;

  /**/
  constructor(props) {
    super(props);
    this.handleResize = this.handleResize.bind(this);
    this.delegateResize = throttle(this.handleResize, 200);
  }

  /**/
  state: State = {
    colCount: 1,
    isExpanded: false,
    width: 0
  };

  /**/
  getColCount(width: number): number {
    if (this.props.colCount) return this.props.colCount;

    if (width >= BREAKPOINTS.MD && width < BREAKPOINTS.LG) {
      return 3;
    } else if (width < BREAKPOINTS.SM) {
      return 1;
    } else if (width >= BREAKPOINTS.LG) {
      return 4;
    }
    return 2;
  }

  /**/
  handleExpand = (isExpanded: boolean) =>
    this.setState({ isExpanded: isExpanded });

  /**/
  handleResize({ width }): void {
    this.setState({
      colCount: this.props.colCount || this.getColCount(width),
      width: width
    });
  }

  /**/
  abstract renderContent(): ReactElement | ReactElement[];

  /**/
  shouldShowButton(): boolean {
    const { counterToShowAll, data } = this.props;
    return !counterToShowAll || data.length > counterToShowAll;
  }

  /**/
  renderShowAllBtn(): ReactElement {
    return (
      this.shouldShowButton() && <ShowMoreLess onChange={this.handleExpand} />
    );
  }

  /**/
  getLabel(): string {
    return (
      this.props.children?.type &&
      this.props.children?.type['label'].toLowerCase()
    );
  }

  /**/
  render(): ReactElement {
    const { data } = this.props;
    const label = this.getLabel();
    const src = `${PLACEHOLDER._PATH}${label}${PLACEHOLDER._SUFFIX}`;

    if (!data || data.length === 0) {
      return (
        <EmptyList
          placeholder={`Add your first ${label}`}
          placeholderImgUrl={src}
        />
      );
    }

    const content = (): ReactElement => (
      <>
        {this.renderContent()}
        {this.renderShowAllBtn()}
      </>
    );

    return (
      <AutoSizer
        children={content}
        className="ServiceEntityList"
        onResize={this.delegateResize}
      />
    );
  }
}

/**
 *
 */
ServiceEntityList.defaultProps = {
  gutter: 10
};
ServiceEntityList.propTypes = {
  colCount: PropTypes.number,
  counterToShowAll: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.object),
  gutter: PropTypes.number,
  onChange: PropTypes.func
};

export default ServiceEntityList;
